import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

type ToastItem = {
  id: string;
  message: string;
  variant: 'error' | 'success';
};

type ToastContextValue = {
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
};

const TOAST_DURATION_MS = 4000;
const ToastContext = createContext<ToastContextValue | null>(null);

const createToastId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((toastId: string) => {
    setToasts((prev) => prev.filter((item) => item.id !== toastId));
  }, []);

  const showToast = useCallback((message: string, variant: 'error' | 'success') => {
    const safeMessage = message.trim();
    if (safeMessage === '') {
      return;
    }
    const id = createToastId();
    setToasts((prev) => {
      const hasSameMessage = prev.some(
        (item) => item.message === safeMessage && item.variant === variant
      );
      if (hasSameMessage) {
        return prev;
      }
      return [...prev, { id, message: safeMessage, variant }];
    });
    window.setTimeout(() => {
      removeToast(id);
    }, TOAST_DURATION_MS);
  }, [removeToast]);

  const showErrorToast = useCallback((message: string) => {
    showToast(message, 'error');
  }, [showToast]);

  const showSuccessToast = useCallback((message: string) => {
    showToast(message, 'success');
  }, [showToast]);

  const contextValue = useMemo<ToastContextValue>(
    () => ({
      showErrorToast,
      showSuccessToast,
    }),
    [showErrorToast, showSuccessToast]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 top-3 z-[100] mx-auto flex w-full max-w-lg flex-col gap-2 px-4"
        data-toast-root="true"
        aria-live="assertive"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 text-sm font-medium text-white shadow-lg ${
              toast.variant === 'error'
                ? 'border-red-300 bg-red-600 dark:border-red-700 dark:bg-red-700'
                : 'border-emerald-300 bg-emerald-600 dark:border-emerald-700 dark:bg-emerald-700'
            }`}
          >
            <p role="alert" data-toast-ignore="true" className="min-w-0 flex-1">
              {toast.message}
            </p>
            <button
              type="button"
              onClick={() => removeToast(toast.id)}
              aria-label="Close notification"
              className={`rounded p-0.5 text-white/90 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-white ${
                toast.variant === 'error' ? 'hover:bg-red-700/70' : 'hover:bg-emerald-700/70'
              }`}
            >
              <span aria-hidden>✕</span>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
